import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  deleteCampaignSpend,
  getLeadConversionsReport,
  saveCampaignSpend,
} from "@/lib/lead-conversions.functions";
import type { LeadReport, SpendChannel } from "@/lib/lead-conversions.types";


export const Route = createFileRoute("/relatorio-leads")({
  head: () => ({
    meta: [
      { title: "Relatório de leads por campanha | Kentro" },
      {
        name: "description",
        content:
          "Painel interno da Kentro com as conversões de leads do site agrupadas por campanha, origem e formulário.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Relatório de leads por campanha | Kentro" },
      { property: "og:description", content: "Painel interno de conversões de leads por campanha." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RelatorioLeadsPage,
});

const PERIODS = [7, 30, 90] as const;

function RelatorioLeadsPage() {
  const fetchReport = useServerFn(getLeadConversionsReport);
  const [password, setPassword] = useState("");
  const [days, setDays] = useState<number>(30);
  const [ticket, setTicket] = useState<string>("0");
  const [report, setReport] = useState<LeadReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load(nextDays = days, nextTicket = ticket) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReport({
        data: { password, days: nextDays, ticket: Number(nextTicket) || 0 },
      });
      if (!res.ok) {
        setReport(null);
        setError("Senha inválida.");
      } else {
        setReport(res.report);
      }
    } catch {
      setError("Não foi possível carregar o relatório agora.");
    } finally {
      setLoading(false);
    }
  }


  return (
    <main className="mx-auto max-w-6xl px-4 pt-28 pb-20 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold tracking-tight">
        Relatório de <span className="gradient-text">leads por campanha</span>
      </h1>
      <p className="mt-2 max-w-2xl text-muted-foreground">
        Cada lead enviado pelo site é gravado com UTMs, click IDs (fbclid, gclid, ttclid) e a origem
        resolvida. Use a senha interna para consultar.
      </p>

      <form
        className="mt-8 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <div className="w-64">
          <Label htmlFor="report-password">Senha interna</Label>
          <Input
            id="report-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <Button
              key={p}
              type="button"
              variant={days === p ? "default" : "outline"}
              onClick={() => {
                setDays(p);
                if (report) void load(p);
              }}
            >
              {p} dias
            </Button>
          ))}
        </div>
        <div className="w-40">
          <Label htmlFor="report-ticket">Ticket médio (R$)</Label>
          <Input
            id="report-ticket"
            type="number"
            min={0}
            step="0.01"
            value={ticket}
            onChange={(e) => setTicket(e.target.value)}
            className="mt-1"
          />
        </div>
        <Button type="submit" disabled={!password || loading} className="gradient-primary text-white">
          {loading ? "Carregando..." : "Ver relatório"}
        </Button>
      </form>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

      {report && (
        <div className="mt-10 space-y-10">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">{report.total}</strong> leads nos últimos {report.days} dias.
          </p>

          <PerformanceSection report={report} />
          <SpendManager password={password} report={report} onChanged={() => void load()} />

          <GroupTable title="Por campanha (utm_campaign)" rows={report.byCampaign} />

          <GroupTable title="Por origem resolvida" rows={report.bySource} />
          <GroupTable title="Por formulário" rows={report.byForm} />

          <section>
            <h2 className="text-xl font-semibold">Últimos leads</h2>
            <div className="mt-3 overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-left text-sm">
                <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
                  <tr>
                    <Th>Data</Th>
                    <Th>Nome</Th>
                    <Th>Contato</Th>
                    <Th>Origem</Th>
                    <Th>Campanha</Th>
                    <Th>Click ID</Th>
                    <Th>Entrada</Th>
                  </tr>
                </thead>
                <tbody>
                  {report.leads.map((l) => (
                    <tr key={l.id} className="border-t border-white/5">
                      <Td>{new Date(l.created_at).toLocaleString("pt-BR")}</Td>
                      <Td>{l.name ?? "—"}</Td>
                      <Td>
                        <div>{l.email ?? "—"}</div>
                        <div className="text-muted-foreground">{l.phone ?? ""}</div>
                      </Td>
                      <Td>
                        <div>{l.resolved_source}</div>
                        <div className="text-xs text-muted-foreground">{l.attribution_method}</div>
                      </Td>
                      <Td>
                        <div>{l.utm_campaign ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {[l.utm_source, l.utm_medium, l.utm_content].filter(Boolean).join(" / ")}
                        </div>
                      </Td>
                      <Td className="max-w-[180px] truncate">
                        {l.fbclid ? `fbclid: ${l.fbclid}` : l.gclid ? `gclid: ${l.gclid}` : "—"}
                      </Td>
                      <Td>{l.landing_path ?? "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

const brl = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function PerformanceSection({ report }: { report: LeadReport }) {
  const p = report.performance;
  return (
    <section>
      <h2 className="text-xl font-semibold">CPA e ROAS por campanha</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Investimento do período cruzado com os leads gravados. Leads por plataforma são contados pelo click ID
        (fbclid, gclid/wbraid/gbraid, ttclid, oppref) com fallback em utm_source.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Investimento total" value={brl(p.spendTotal)} hint={`Meta ${brl(p.spendMeta)} · Google ${brl(p.spendGoogle)} · Extra ${brl(p.spendExtra)}`} />
        <Kpi label="Leads" value={String(p.leads)} />
        <Kpi label="CPA geral" value={p.cpa === null ? "—" : brl(p.cpa)} />
        <Kpi
          label="ROAS geral"
          value={p.roas === null ? "—" : `${p.roas.toFixed(2)}x`}
          hint={p.ticket > 0 ? `Receita estimada ${brl(p.revenue)} (ticket ${brl(p.ticket)})` : "Informe o ticket médio para o ROAS"}
        />
      </div>

      {p.spendSemCampanha > 0 && (
        <p className="mt-3 text-xs text-muted-foreground">
          {brl(p.spendSemCampanha)} de investimento sem campanha definida ficam agrupados em “(sem campanha)”.
        </p>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>Campanha</Th>
              <Th>Leads</Th>
              <Th>Meta</Th>
              <Th>Google</Th>
              <Th>TikTok</Th>
              <Th>ChatGPT</Th>
              <Th>Sem click ID</Th>
              <Th>Investimento</Th>
              <Th>CPA</Th>
              <Th>ROAS</Th>
            </tr>
          </thead>
          <tbody>
            {p.campaigns.length === 0 ? (
              <tr>
                <Td className="text-muted-foreground">Sem dados no período.</Td>
              </tr>
            ) : (
              p.campaigns.map((c) => (
                <tr key={c.campaign} className="border-t border-white/5">
                  <Td>{c.campaign}</Td>
                  <Td>{c.leads}</Td>
                  <Td>{c.leadsMeta}</Td>
                  <Td>{c.leadsGoogle}</Td>
                  <Td>{c.leadsTikTok}</Td>
                  <Td>{c.leadsChatGpt}</Td>
                  <Td>{c.leadsSemClickId}</Td>
                  <Td>
                    <div>{brl(c.spendTotal)}</div>
                    <div className="text-xs text-muted-foreground">
                      M {brl(c.spendMeta)} · G {brl(c.spendGoogle)} · E {brl(c.spendExtra)}
                    </div>
                  </Td>
                  <Td>{c.cpa === null ? "—" : brl(c.cpa)}</Td>
                  <Td>{c.roas === null ? "—" : `${c.roas.toFixed(2)}x`}</Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const CHANNELS: { key: SpendChannel; label: string }[] = [
  { key: "meta", label: "Meta" },
  { key: "google", label: "Google" },
  { key: "extra", label: "Extra" },
];

function SpendManager({
  password,
  report,
  onChanged,
}: {
  password: string;
  report: LeadReport;
  onChanged: () => void;
}) {
  const save = useServerFn(saveCampaignSpend);
  const remove = useServerFn(deleteCampaignSpend);
  const today = new Date().toISOString().slice(0, 10);
  const [channel, setChannel] = useState<SpendChannel>("meta");
  const [campaign, setCampaign] = useState("");
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await save({
        data: {
          password,
          channel,
          campaign,
          period_start: start,
          period_end: end,
          amount: Number(amount) || 0,
        },
      });
      if (!res.ok) setMsg("Senha inválida.");
      else {
        setAmount("");
        onChanged();
      }
    } catch {
      setMsg("Não foi possível salvar o investimento.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2 className="text-xl font-semibold">Investimentos (Meta, Google e extras)</h2>
      <form
        className="mt-3 flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div>
          <Label htmlFor="spend-channel">Canal</Label>
          <select
            id="spend-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as SpendChannel)}
            className="mt-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {CHANNELS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="w-56">
          <Label htmlFor="spend-campaign">Campanha (utm_campaign)</Label>
          <Input
            id="spend-campaign"
            value={campaign}
            placeholder="vazio = geral"
            onChange={(e) => setCampaign(e.target.value)}
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="spend-start">Início</Label>
          <Input id="spend-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="spend-end">Fim</Label>
          <Input id="spend-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" />
        </div>
        <div className="w-32">
          <Label htmlFor="spend-amount">Valor (R$)</Label>
          <Input
            id="spend-amount"
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1"
          />
        </div>
        <Button type="submit" disabled={busy || !amount}>
          {busy ? "Salvando..." : "Adicionar"}
        </Button>
      </form>
      {msg && <p className="mt-2 text-sm text-destructive">{msg}</p>}

      {report.spends.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>Canal</Th>
                <Th>Campanha</Th>
                <Th>Período</Th>
                <Th>Valor</Th>
                <Th>Ação</Th>
              </tr>
            </thead>
            <tbody>
              {report.spends.map((s) => (
                <tr key={s.id} className="border-t border-white/5">
                  <Td>{CHANNELS.find((c) => c.key === s.channel)?.label ?? s.channel}</Td>
                  <Td>{s.campaign || "(geral)"}</Td>
                  <Td>
                    {new Date(`${s.period_start}T12:00:00`).toLocaleDateString("pt-BR")} —{" "}
                    {new Date(`${s.period_end}T12:00:00`).toLocaleDateString("pt-BR")}
                  </Td>
                  <Td>{brl(s.amount)}</Td>
                  <Td>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        await remove({ data: { password, id: s.id } });
                        onChanged();
                      }}
                    >
                      Remover
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


function GroupTable({
  title,
  rows,
}: {
  title: string;
  rows: LeadReport["byCampaign"];
}) {
  return (
    <section>
      <h2 className="text-xl font-semibold">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Nenhum lead no período.</p>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>Chave</Th>
                <Th>Leads</Th>
                <Th>Campanhas distintas</Th>
                <Th>Último lead</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-white/5">
                  <Td>{r.key}</Td>
                  <Td>{r.total}</Td>
                  <Td>{r.campaigns}</Td>
                  <Td>{r.lastAt ? new Date(r.lastAt).toLocaleString("pt-BR") : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
